import React, { useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { truncateText } from './utils';
import type { ItemBrand } from './types';

interface Props {
  loading: boolean;
  brands: ItemBrand[];
  pageNo: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  onEdit: (b: ItemBrand) => void;
  onView: (id: number) => void; // still called when opening modal (optional side-effects)
  onDelete: (id: number) => void;

  /** Optional: render the items list for a brand inside the modal */
  renderBrandItems?: (brandId: number) => React.ReactNode;
}

const ItemBrandsTable: React.FC<Props> = ({
  loading,
  brands,
  pageNo,
  pageSize,
  onPrev,
  onNext,
  onEdit,
  onView,
  onDelete,
  renderBrandItems,
}) => {
  // --- local state for the in-page View modal ---
  const [showView, setShowView] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);

  const selectedBrand = useMemo(
    () => brands.find(b => b.id === selectedBrandId) || null,
    [brands, selectedBrandId]
  );

  const openView = (id: number) => {
    setSelectedBrandId(id);
    setShowView(true);
    // still notify parent if needed (e.g., to fetch items or track analytics)
    onView?.(id);
  };

  const closeView = () => {
    setShowView(false);
    setSelectedBrandId(null);
  };

  return (
    <>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">Item Brands</h5>
          <button
            className="btn btn-sm btn-success"
            data-bs-toggle="modal"
            data-bs-target="#add_brand"
            disabled={loading}
          >
            <i className="ti ti-plus" /> Add Brand
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map((b) => (
                    <tr key={b.id}>
                      <td className="truncate-name">{truncateText(b.brandName, 14)}</td>
                      <td className="truncate-description">
                        {truncateText(b.description || '', 29)}
                      </td>
                      <td className="d-flex gap-2">
                        <Button
                      className="btn btn-sm btn-icon"
                      data-bs-toggle="modal"
                      data-bs-target="#edit_brand"
                      variant="outline-danger"
                      onClick={() => onEdit(b)}
                      disabled={loading}

                    >
                      <i className="ti ti-edit" />
                      </Button>

                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => openView(b.id)}
                          disabled={loading}
                          title="View"
                        >
                          <i className="ti ti-eye" />
                        </Button>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDelete(b.id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <i className="ti ti-trash" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!brands.length && (
                    <tr>
                      <td colSpan={3} className="text-center py-4">
                        No brands found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card-footer d-flex justify-content-between align-items-center">
          <button
            className="btn btn-sm btn-secondary"
            onClick={onPrev}
            disabled={pageNo === 1 || loading}
          >
            Previous
          </button>
          <span>Page {pageNo}</span>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onNext}
            disabled={brands.length < pageSize || loading}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Brand Modal (in-page) */}
      <Modal show={showView} onHide={closeView} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedBrand ? `Brand: ${selectedBrand.brandName}` : 'Brand Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBrand ? (
            <>
              <div className="mb-3">
                <strong>Name:</strong> {selectedBrand.brandName}
              </div>
              <div className="mb-3">
                <strong>Description:</strong>{' '}
                {selectedBrand.description ? selectedBrand.description : <em>—</em>}
              </div>

              {/* Optional: render items for this brand (provided by parent) */}
              {renderBrandItems && (
                <div className="mt-4">
                  <h6 className="mb-2">Items under this brand</h6>
                  <div>{renderBrandItems(selectedBrand.id)}</div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted">Brand not found.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeView}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ItemBrandsTable;
